import { useEffect, useState } from "react";
import Button from "../common/Button";
import BaseModal from "../modals/BaseModal";
import { normalizeApiError } from "../../utils/errors";
import { getRoles } from "../../services/teamApi";
import { TEAM_STATUS_OPTIONS } from "../../utils/constants";

function formatRoleName(name) {
  return String(name || "")
    .trim()
    .split(/[_-]+/g)
    .filter(Boolean)
    .map((word) => {
      if (word.toUpperCase() === "AI") {
        return "AI";
      }

      const normalizedWord = word.toLowerCase();
      return normalizedWord.charAt(0).toUpperCase() + normalizedWord.slice(1);
    })
    .join(" ");
}

function normalizeRoleKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function buildRoleOptions(items) {
  return Array.isArray(items)
    ? items.map((role) => ({
        role_id: Number(role?.role_id),
        name: role?.name || "",
        label: role?.label || formatRoleName(role?.name),
      }))
    : [];
}

function findMatchingRole(value, roleOptions) {
  if (!value || !Array.isArray(roleOptions) || !roleOptions.length) {
    return null;
  }

  const exactMatch = roleOptions.find((role) => role.label === value);

  if (exactMatch) {
    return exactMatch;
  }

  const normalizedValue = normalizeRoleKey(value);

  return (
    roleOptions.find(
      (role) =>
        normalizeRoleKey(role.name) === normalizedValue ||
        normalizeRoleKey(role.label) === normalizedValue,
    ) || null
  );
}

function buildInitialState(member) {
  const rawPhone = member?.phone || "";
  return {
    name: member?.name || "",
    email: member?.email || "",
    phone: rawPhone === "--" ? "" : rawPhone,
    role: member?.role || "",
    status: member?.status || TEAM_STATUS_OPTIONS[0],
    password: "",
  };
}

export default function TeamMemberFormModal({
  open,
  mode,
  member,
  busy,
  onClose,
  onSubmit,
}) {
  const [form, setForm] = useState(buildInitialState(member));
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm(buildInitialState(member));
  }, [member, mode, open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    async function loadRoles() {
      setRolesLoading(true);
      setRolesError("");

      try {
        const response = await getRoles({
          signal: controller.signal,
        });

        if (!active) {
          return;
        }

        const items = Array.isArray(response)
          ? response
          : Array.isArray(response?.items)
            ? response.items
            : [];

        setRoles(buildRoleOptions(items));
      } catch (errorValue) {
        const normalizedError = normalizeApiError(errorValue);

        if (!active || normalizedError.isAbort) {
          return;
        }

        setRoles([]);
        setRolesError(normalizedError.message || "Unable to load roles right now.");
      } finally {
        if (active) {
          setRolesLoading(false);
        }
      }
    }

    loadRoles();

    return () => {
      active = false;
      controller.abort();
    };
  }, [open]);

  useEffect(() => {
    if (!open || rolesLoading || !roles.length) {
      return;
    }

    setForm((current) => {
      const matchedRole = findMatchingRole(current.role, roles);

      if (matchedRole && current.role !== matchedRole.label) {
        return {
          ...current,
          role: matchedRole.label,
        };
      }

      if (!current.role) {
        return {
          ...current,
          role: roles[0].label,
        };
      }

      return current;
    });
  }, [open, roles, rolesLoading]);

  const title = mode === "edit" ? "Edit Team Member" : "Add Team Member";
  const meta =
    mode === "edit"
      ? "Update the selected team member."
      : "Add a new team member.";

  return (
    <BaseModal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={title}
      meta={meta}
      modalId={`team-member-${mode}-modal`}
      className="team-modal"
    >
      <form
        className="team-member-form"
        onSubmit={(event) => {
          event.preventDefault();

          const payload = {
            name: form.name.trim(),
            email: form.email.trim(),
            phone: form.phone.trim(),
            role: form.role,
            status: form.status,
          };
          if (form.password) {
            payload.password = form.password;
          }
          onSubmit(payload);
        }}
      >
        <div className="team-modal-grid">
          <label className="outbound-field">
            <span className="outbound-label">Full Name</span>
            <input
              className="outbound-input"
              value={form.name}
              onChange={(event) =>
                setForm((current) => ({ ...current, name: event.target.value }))
              }
              required
              disabled={busy}
            />
          </label>
          <label className="outbound-field">
            <span className="outbound-label">Email</span>
            <input
              className="outbound-input"
              type="email"
              value={form.email}
              onChange={(event) =>
                setForm((current) => ({ ...current, email: event.target.value }))
              }
              required
              disabled={busy}
            />
          </label>
          <label className="outbound-field">
            <span className="outbound-label">Phone</span>
            <input
              className="outbound-input"
              type="tel"
              value={form.phone}
              onChange={(event) =>
                setForm((current) => ({ ...current, phone: event.target.value }))
              }
              required
              disabled={busy}
            />
          </label>
          <label className="outbound-field">
            <span className="outbound-label">Role</span>
            <select
              className="team-modal-select"
              value={form.role}
              onChange={(event) =>
                setForm((current) => ({ ...current, role: event.target.value }))
              }
              disabled={busy || rolesLoading || !roles.length}
            >
              {rolesLoading ? (
                <option value="">Loading roles...</option>
              ) : roles.length ? (
                roles.map((option) => (
                  <option key={option.role_id} value={option.label}>
                    {option.label}
                  </option>
                ))
              ) : (
                <option value="">
                  {rolesError ? "Unable to load roles" : "No roles found"}
                </option>
              )}
            </select>
            {rolesError ? <span className="error-message">{rolesError}</span> : null}
          </label>
          <label className="outbound-field">
            <span className="outbound-label">
              {mode === "edit" ? "New Password" : "Password"}
              {mode === "edit" && (
                <span style={{ fontWeight: 400, color: "#888", marginLeft: 6 }}>
                  (leave blank to keep current)
                </span>
              )}
            </span>
            <input
              className="outbound-input"
              type="password"
              value={form.password}
              onChange={(event) =>
                setForm((current) => ({ ...current, password: event.target.value }))
              }
              required={mode === "add"}
              minLength={8}
              placeholder={mode === "edit" ? "Leave blank to keep current" : "Min. 8 characters"}
              disabled={busy}
              autoComplete="new-password"
            />
          </label>
          <label className="outbound-field">
            <span className="outbound-label">Status</span>
            <select
              className="team-modal-select"
              value={form.status}
              onChange={(event) =>
                setForm((current) => ({ ...current, status: event.target.value }))
              }
              disabled={busy}
            >
              {TEAM_STATUS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          {/* <label className="outbound-field">
            <span className="outbound-label">Calls Today</span>
            <input
              className="outbound-input"
              type="number"
              min="0"
              value={form.calls_today}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  calls_today: event.target.value,
                }))
              }
              disabled={busy}
            />
          </label> */}
          {/* <label className="outbound-field">
            <span className="outbound-label">Bookings Today</span>
            <input
              className="outbound-input"
              type="number"
              min="0"
              value={form.bookings_today}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  bookings_today: event.target.value,
                }))
              }
              disabled={busy}
            />
          </label> */}
          {/* <label className="outbound-field">
            <span className="outbound-label">Avg Duration (Seconds)</span>
            <input
              className="outbound-input"
              type="number"
              min="0"
              value={form.average_duration_seconds}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  average_duration_seconds: event.target.value,
                }))
              }
              disabled={busy}
            />
          </label> */}
          {/* <label className="outbound-field">
            <span className="outbound-label">Conversion (%)</span>
            <input
              className="outbound-input"
              type="number"
              min="0"
              max="100"
              value={form.conversion_rate}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  conversion_rate: event.target.value,
                }))
              }
              disabled={busy}
            />
          </label> */}
          {/* <label className="outbound-field team-modal-field-span">
            <span className="outbound-label">Last Active</span>
            <input
              className="outbound-input"
              value={form.last_active}call
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  last_active: event.target.value,
                }))
              }
              disabled={busy}
            />
          </label> */}
        </div>

        <div className="modal-actions">
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="gold" disabled={busy || rolesLoading}>
            {busy
              ? mode === "edit"
                ? "Saving..."
                : "Adding..."
              : mode === "edit"
                ? "Save Changes"
                : "Add Team Member"}
          </Button>
        </div>
      </form>
    </BaseModal>
  );
}
